import {
  Dispatch,
  SetStateAction,
  useCallback,
  useEffect,
  useMemo,
  useRef,
} from 'react'

import { usePathname, useRouter } from 'next/navigation'
import useLocalStorage from 'react-use/lib/useLocalStorage'

import { getAllChains, hasCapability } from '~/constants/chains'
import { useAuth } from '~/context/AuthProvider'
import { useActiveOrg } from '~/hooks/org'

export function useAllChainIds(): number[] {
  const { featureFlags } = useActiveOrg()
  return useMemo(
    () =>
      getAllChains()
        .filter(
          (chain) => !chain.rolloutFlag || !!featureFlags?.[chain.rolloutFlag],
        )
        .map((chain) => chain.chainId),
    [featureFlags],
  )
}

export function useAccountImportChainIds(): number[] {
  const allChainIds = useAllChainIds()
  return useMemo(
    () => allChainIds.filter((chainId) => hasCapability(chainId, 'accounting')),
    [allChainIds],
  )
}

function useShowSmallBalances(storageKey: string): {
  showSmallBalances: boolean
  setShowSmallBalances: Dispatch<SetStateAction<boolean | undefined>>
} {
  const [showSmallBalances, setShowSmallBalances] = useLocalStorage<boolean>(
    storageKey,
    false,
  )

  return { showSmallBalances: showSmallBalances ?? false, setShowSmallBalances }
}

export const useSmartAccountsShowSmallBalances = () =>
  useShowSmallBalances('splits.smartAccounts.showSmallBalances')

// useDialogRoutePath updates the browser path when a dialog/modal is opened or closed.
// It pushes a path when the dialog opens and reverts to the original path when it closes.
export function useDialogRoutePath({
  getPath,
  globalModal,
  closeModal,
}: {
  getPath: () => string | undefined
  globalModal?: boolean
  closeModal: () => void
}) {
  const { globalModalState } = useAuth()
  const router = useRouter()
  const dialogPath = getPath()
  const currentPath = usePathname()
  const originalPathRef = useRef<string>(currentPath)
  // Do we hold a pushed entry right now, and is this dialog finished? They are
  // not the same fact. React undoes and redoes a mount in development, thus the
  // first one goes back to false. The second one never does, or the effect
  // pushes the path again after each restore.
  const pathUpdatedRef = useRef<boolean>(false)
  const dialogClosedRef = useRef<boolean>(false)

  // Put the path back the way we found it. replaceState, not history.back():
  // pushing the dialog path never changed the rendered route, so overwriting
  // the entry re-syncs the URL without navigating. Going back navigates for
  // real, and it only lands on the right page when this dialog's entry is the
  // one on top — a dialog swapped out for another leaves its entry behind,
  // and the user ends up on whatever preceded it, even another team's page.
  const restorePath = useCallback(() => {
    if (!pathUpdatedRef.current) return
    // Only our own entry is ours to overwrite. Unmounting also happens when
    // the user navigates away with the dialog open, and by then the entry
    // belongs to the page they went to — restoring would leave that page
    // rendered under this dialog's URL.
    if (!window.history.state?.dialogRoutePath) return
    pathUpdatedRef.current = false
    // A fresh object, not the state we replaced. Next.js ignores a state that
    // holds its own __NA flag: it moves the address bar, but it does not tell
    // the router. usePathname then keeps the dialog path, and the layout keeps
    // the navigation hidden. Next.js copies its router data into this object.
    // The flag tells AppContainer this pathname change is a URL re-sync, not
    // the user leaving the page, so it keeps the global modal open when the
    // dialog was merely swapped for another one.
    window.history.replaceState(
      { dialogRouteRestored: true },
      '',
      originalPathRef.current,
    )
  }, [])

  // Also restore when the dialog unmounts: its content can be replaced (or
  // hidden while the wallet connect modal is open) before the close effect
  // below ever runs.
  useEffect(() => restorePath, [restorePath])

  // Close dialog on back button press.
  useEffect(() => {
    const onPopState = () => {
      if (globalModalState.isOpen) {
        closeModal()
      }
    }

    window.addEventListener('popstate', onPopState, { once: true })

    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [closeModal, globalModalState.isOpen])

  useEffect(() => {
    // If we moved to a different path while the dialog was open, close the modal.
    if (currentPath !== dialogPath && currentPath !== originalPathRef.current) {
      closeModal()
      return
    }

    // Return to original path if modal is closed.
    if (!globalModalState.isOpen && currentPath !== originalPathRef.current) {
      restorePath()
      dialogClosedRef.current = true
      return
    }

    // If the dialog doesn't have a path, do nothing.
    if (!dialogPath) return

    // No need to push dialog path if we're already there.
    if (currentPath === dialogPath) return

    // Ensure we don't change path if this is a global modal, and it is not open yet.
    if (globalModal && !globalModalState.isOpen) return

    // Push the dialog path when the modal opens
    if (!pathUpdatedRef.current && !dialogClosedRef.current) {
      window.history.pushState({ dialogRoutePath: true }, '', dialogPath)
      pathUpdatedRef.current = true
      router.prefetch(dialogPath)
    }
  }, [
    closeModal,
    currentPath,
    dialogPath,
    globalModal,
    restorePath,
    router,
    globalModalState.isOpen,
  ])
}
